import {
  BODY_MEASUREMENT_CATALOG,
  type BodyMeasurementTypeKey,
  trendAxisRange,
  trendMeasurementType,
} from "@/lib/body-measurements";
import type { BodyMeasurementTrendConfig } from "@/lib/schema";
import type { WidgetSettingsEditorProps } from "@/widgets/types";

type Props = WidgetSettingsEditorProps<BodyMeasurementTrendConfig>;

export function SettingsEditor({ config, onChange, errors = {} }: Props) {
  const selected = trendMeasurementType(config);
  const range = trendAxisRange(config, selected.unit);

  const selectType = (key: BodyMeasurementTypeKey) => {
    const next = BODY_MEASUREMENT_CATALOG.find((t) => t.key === key);
    if (!next || next.key === selected.key) return;
    onChange({
      measurementType: key,
      // A range is expressed in the old unit, so it cannot carry over to a
      // measurement in a different one.
      axisRange: next.unit === selected.unit ? range : undefined,
      // An untouched title follows the measurement it graphs.
      label: config.label.trim() === selected.label ? next.label : config.label,
      // Clear the pre-split shape so the saved config has one meaning.
      types: undefined,
      axisRanges: undefined,
    });
  };

  const setAxisBound = (bound: "minimum" | "maximum", raw: string) => {
    const current = range ?? { minimum: Number.NaN, maximum: Number.NaN };
    const next = {
      ...current,
      [bound]: raw === "" ? Number.NaN : Number(raw),
    };
    const cleared =
      !Number.isFinite(next.minimum) && !Number.isFinite(next.maximum);
    onChange({
      axisRange: cleared ? undefined : next,
      axisRanges: undefined,
    });
  };

  const boundValue = (bound: "minimum" | "maximum"): string => {
    const v = range?.[bound];
    return typeof v === "number" && Number.isFinite(v) ? String(v) : "";
  };

  return (
    <div
      className="widget-settings"
      data-testid={`widget-settings-${config.id}`}
    >
      <div className="setting-row">
        <label className="setting-label" htmlFor={`label-${config.id}`}>
          Tittel
        </label>
        <div className="setting-control">
          <input
            id={`label-${config.id}`}
            className="input"
            type="text"
            value={config.label}
            onChange={(e) => onChange({ label: e.target.value })}
            data-testid="widget-label-input"
          />
          {errors.label ? (
            <p className="field-error" data-testid="widget-error-label">
              {errors.label}
            </p>
          ) : null}
        </div>
      </div>
      <div className="setting-row">
        <span className="setting-label">Måling</span>
        <div className="setting-control">
          {BODY_MEASUREMENT_CATALOG.map((t) => (
            <label className="check-option" key={t.key}>
              <input
                type="radio"
                name={`measurement-type-${config.id}`}
                checked={selected.key === t.key}
                onChange={() => selectType(t.key)}
                data-testid={`body-measurement-type-toggle-${t.key}`}
              />
              {t.label} ({t.unit})
            </label>
          ))}
          <p className="hint">
            Én måling per graf — legg til et widget til for en annen måling.
          </p>
        </div>
      </div>
      <div className="setting-row">
        <span className="setting-label">Akse {selected.unit}</span>
        <div className="setting-control">
          <div className="axis-range-inputs">
            <input
              className="input"
              type="number"
              step={0.1}
              placeholder="Min"
              aria-label={`Akse ${selected.unit} minimum`}
              value={boundValue("minimum")}
              onChange={(e) => setAxisBound("minimum", e.target.value)}
              data-testid={`axis-minimum-input-${selected.unit}`}
            />
            <span className="axis-range-separator">–</span>
            <input
              className="input"
              type="number"
              step={0.1}
              placeholder="Maks"
              aria-label={`Akse ${selected.unit} maksimum`}
              value={boundValue("maximum")}
              onChange={(e) => setAxisBound("maximum", e.target.value)}
              data-testid={`axis-maximum-input-${selected.unit}`}
            />
          </div>
          <p className="hint">Tomt = automatisk skala.</p>
          {errors[`axis-${selected.unit}`] ? (
            <p
              className="field-error"
              data-testid={`widget-error-axis-${selected.unit}`}
            >
              {errors[`axis-${selected.unit}`]}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
